import { Box, Flex, Grid, GridItem } from '@sequencehq/core-components'
import { ScheduleTimelineDataSeries } from './ScheduleTimelineDataSeries/ScheduleTimelineDataSeries'
import { MouseEventHandler, useMemo, useState } from 'react'
import {
  TIMELINE_AXIS_HEIGHT_PX,
  TIMELINE_AXIS_Z_INDEX,
  TIMELINE_LABELS_Z_INDEX,
  TIMELINE_LINKS_Z_INDEX
} from './timeline.constants'
import {
  ScheduleTimelineLeftLinks,
  ScheduleTimelineRightLinks
} from './ScheduleTimelineLinks/ScheduleTimelineLinks'
import { useTimelineDimensions } from './hooks/useTimelineDimensions'
import { ScheduleTimelineAxis } from './ScheduleTimelineAxis/ScheduleTimelineAxis'
import { hoverPositionAtom } from './atoms/timelineUi.atom'
import { useAtom } from 'jotai'
import { ScheduleTimelineLabels } from './ScheduleTimelineLabels'

const useScheduleTimelineGrid = () => {
  const [, setHoverPosition] = useAtom(hoverPositionAtom)

  const setNewHoverPosition: MouseEventHandler<HTMLDivElement> = useMemo(() => {
    return e => {
      const target = e.target as HTMLDivElement
      const offsetPosition = Math.floor(
        e.clientX - target.getBoundingClientRect().left
      )

      if (offsetPosition) {
        setHoverPosition(offsetPosition)
      }
    }
  }, [setHoverPosition])

  return {
    setHoverPosition,
    setNewHoverPosition
  }
}

/**
 * The schedule timeline uses a bit of grid and sticky fanciness
 * to give us a decent combination of performance and extensibility.
 *
 * The grid, as we see from the areas, is broken up into four major
 * areas:
 * - axis: The timeline axis that contains the months/weeks and
 *   the day indicators
 * - timeline: The timeline visualisation itself
 * - leftLinks/rightLinks: Links on the left/right of the timeline
 *   which overlay the timeline and provide convenient links to
 *   offscreen parts of the timeline.
 *
 * The left/right links especially benefit from the sticky/grid approach,
 * as otherwise we would struggle to provide an 'absolute positioned' style
 * element that will also react to a vertical scroll inline with the timeline.
 * We could do all of this using JS, but it would 'lag' behind the scroll. With
 * this approach, we benefit from super smooth movement of the links as we scroll.
 */

export const ScheduleTimelineGrid = () => {
  /**
   * We need to reference the timeline container height in order
   * to correctly set the height of the day indicators. This is
   * because we need them to be placed in a sticky element to avoid
   * scrolling, but that sticky element itself cannot be 100% height
   * as it would overlap everything else (or be hidden, which is
   * also suboptimal).
   */
  const { timelineWidth } = useTimelineDimensions()
  const hook = useScheduleTimelineGrid()

  return (
    <Grid
      position="relative"
      gridTemplateAreas={`
  "axis axis axis axis"
  "leftLinks timeline-labels timeline rightLinks"     
`}
      gridTemplateColumns={'0 1fr auto'}
      gridTemplateRows={`${TIMELINE_AXIS_HEIGHT_PX}px auto`}
      height="fit-content"
      minHeight="100%"
      width={`${timelineWidth}px`}
      onMouseMove={e => {
        hook.setNewHoverPosition(e)
      }}
      onMouseLeave={() => {
        hook.setHoverPosition(null)
      }}
    >
      <GridItem
        width={`${timelineWidth}px`}
        gridArea="axis"
        position="sticky"
        top="0"
        zIndex={TIMELINE_AXIS_Z_INDEX}
      >
        <ScheduleTimelineAxis />
      </GridItem>
      <GridItem
        gridArea="leftLinks"
        position="sticky"
        left="0"
        width="0px"
        height="100%"
        zIndex={TIMELINE_LINKS_Z_INDEX}
        pointerEvents="none"
      >
        <ScheduleTimelineLeftLinks />
      </GridItem>
      <GridItem
        gridArea="timeline-labels"
        position="absolute"
        zIndex={TIMELINE_LABELS_Z_INDEX}
        top="0"
        left="0"
        width={timelineWidth}
        height="100%"
      >
        <ScheduleTimelineLabels />
      </GridItem>
      <GridItem gridArea="timeline" pointerEvents="none">
        <ScheduleTimelineDataSeries />
      </GridItem>
      <GridItem
        gridArea="rightLinks"
        position="sticky"
        right="0"
        width="0px"
        height="100%"
        zIndex={TIMELINE_LINKS_Z_INDEX}
      >
        <Box
          position="absolute"
          right="0px"
          height="100%"
          width="250px"
          pointerEvents="none"
        >
          <ScheduleTimelineRightLinks />
        </Box>
      </GridItem>
    </Grid>
  )
}
